/**
 * EntityRow — redesigned entity matching row.
 *
 * States: ok | confirmed | needs_review | missing | corrected | derived | reset
 * Each state has a left-border accent color, status dot, and contextual CTAs.
 */
import React, { useState } from "react";
import type { EntityTypeConfig, EntityMatch, EntitySearchResult, CorrectionItem } from "../api/types";
import { ENTITY_FEEDBACK_CATEGORIES } from "../api/types";
import { stripPkPrefix } from "../helpers/entityUtils";

/* ── Helpers ── */

const METHOD_LABELS: Record<string, string> = {
  EXACT: "Esatta", LEARNED: "Appreso", FUZZY: "Simile",
  CUSTOM_RULE: "Regola", VECTOR: "Semantico", content_match: "Contenuto",
  content_match_impianto: "Contenuto", impianto_unico: "Unico imp.",
  fallback: "Fallback", fk_lookup: "FK", cascade: "Cascata",
  filter_desc_prest: "Contratto", regola_2_impianti_elettrico: "Regola elettrico",
  filter_by_chiamante_edifici: "Filtro chiamante",
  SENDER_FALLBACK: "Mittente fallback", sender_fallback: "Estratto dall'email",
  email_match: "Riconosciuto per email", match_by_email_chiamante: "Riconosciuto per email",
};

function methodLabel(m: string): string {
  return METHOD_LABELS[m] ?? METHOD_LABELS[m.toUpperCase()] ?? m;
}

function entityFallbackLabel(entity: { pk: string; fields: Record<string, unknown> } | null | undefined): string | null {
  if (!entity) return null;
  const f = entity.fields;
  return String(f.descrizione ?? f.nome ?? f.ragione_sociale ?? f.nominativo ?? f.desc_tipo ?? entity.pk);
}

function codiceClienteChip(fields: Record<string, unknown> | undefined): string | null {
  if (!fields) return null;
  const cc = fields.codice_cliente;
  if (!cc || String(cc).trim() === "") return null;
  return String(cc).trim();
}

function subtitle(key: string, fields: Record<string, unknown> | undefined): string | null {
  if (!fields) return null;
  if (key === "edificio") {
    const parts = [fields.indirizzo, fields.comune].filter(Boolean).map(String);
    return parts.length ? parts.join(" · ") : null;
  }
  if (key === "impianto") return fields.descrizione ? String(fields.descrizione) : null;
  if (key === "chiamante") {
    const parts = [fields.email, fields.telefono].filter(Boolean).map(String);
    return parts.length ? parts.join(" · ") : null;
  }
  if (key === "amministratore") return fields.telefono ? String(fields.telefono) : null;
  return null;
}

/* ── Detail fields helpers ── */

const HIDDEN_FIELDS = /^(embedding|codaz|hashrow|_score|insert_date|update_date|insert_ts|update_ts|created_at|updated_at)$/i;

function fieldLabel(key: string): string {
  const MAP: Record<string, string> = {
    codice_edificio: "Cod. edificio", codice_impianto: "Cod. impianto", codice_cliente: "Cod. cliente",
    desc_tipo: "Tipo impianto", desc_prest: "Contratto", tipo_utilizzo_desc: "Tipo utilizzo",
    referente_nome: "Referente", rtel: "Tel. referente", rcell: "Cell. referente",
    remail: "Email referente", note_varie: "Note", zonalogica: "Zona logica",
    tipo_edif: "Tipo edificio", ragione_sociale: "Ragione sociale",
    amministratore_id: "Cod. amministratore", ref_tipo_edificio: "Rif. tipo",
  };
  return MAP[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isEmptyVal(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    return t === "" || t === "null" || t === "none" || t === "(non definito)" || t === "non definito";
  }
  return false;
}

function getDetailFields(
  fields: Record<string, unknown> | undefined,
  displayFieldsList: string[],
): { primary: [string, string][]; extra: [string, string][] } {
  if (!fields) return { primary: [], extra: [] };
  const primarySet = new Set(displayFieldsList);
  const primary: [string, string][] = [];
  const extra: [string, string][] = [];
  // Show display_fields in config order first
  for (const k of displayFieldsList) {
    if (k in fields && !isEmptyVal(fields[k]) && !HIDDEN_FIELDS.test(k)) {
      primary.push([k, String(fields[k])]);
    }
  }
  // Then everything else
  for (const [k, v] of Object.entries(fields)) {
    if (!isEmptyVal(v) && !HIDDEN_FIELDS.test(k) && !primarySet.has(k)) {
      extra.push([k, String(v)]);
    }
  }
  return { primary, extra };
}

/* ── Types ── */

type RowState = "ok" | "confirmed" | "needs_review" | "missing" | "corrected" | "derived" | "reset" | "new_entity";

export interface EntityRowProps {
  entityType: EntityTypeConfig;
  match: EntityMatch;
  ticketIndex: number;
  verdict?: "ok" | "corrected";
  correctedTo?: EntitySearchResult | null;
  isDerived?: boolean;
  /** Parent entity: show as context, only "Cambia" (no "Conferma"). Auto-confirmed via child. */
  isParent?: boolean;
  isReset?: boolean;
  readOnly?: boolean;
  onConfirm: () => void;
  onOpenSearch: () => void;
  onUndo: () => void;
  onPickAlt: (alt: { entity: { pk: string; fields: Record<string, unknown> }; display_label: string; score: number }) => void;
  extractedHint?: string | null;
  pendingEntity?: EntitySearchResult | null;
  onConfirmWithFeedback?: (entity: EntitySearchResult, categories: string[], note: string) => void;
  onCancelPending?: () => void;
}

/* ── Accent map ── */

const ACCENT: Record<RowState, { bar: string; bg: string }> = {
  ok:           { bar: "var(--indaco-success)",        bg: "var(--indaco-white)" },
  confirmed:    { bar: "var(--indaco-success)",        bg: "var(--indaco-success-light)" },
  needs_review: { bar: "var(--indaco-warning)",        bg: "var(--indaco-warning-light)" },
  missing:      { bar: "var(--indaco-danger)",         bg: "var(--indaco-danger-light)" },
  corrected:    { bar: "var(--indaco-brand-primary)",  bg: "var(--indaco-info-light)" },
  derived:      { bar: "var(--indaco-gray-400)",       bg: "var(--indaco-white)" },
  reset:        { bar: "var(--indaco-warning)",        bg: "var(--indaco-warning-light)" },
  new_entity:   { bar: "var(--indaco-new-entity)",     bg: "var(--indaco-new-entity-light)" },
};

const STATUS_DOT: Record<RowState, { bg: string; fg: string; icon: string }> = {
  ok:           { bg: "var(--indaco-success-light)", fg: "var(--indaco-success)", icon: "check" },
  confirmed:    { bg: "var(--indaco-success)", fg: "var(--indaco-white)", icon: "check" },
  needs_review: { bg: "var(--indaco-warning-light)", fg: "var(--indaco-warning)", icon: "priority_high" },
  missing:      { bg: "var(--indaco-danger-light)", fg: "var(--indaco-danger)", icon: "close" },
  corrected:    { bg: "var(--indaco-brand-primary)", fg: "var(--indaco-white)", icon: "edit" },
  derived:      { bg: "var(--indaco-gray-100)", fg: "var(--indaco-gray-600)", icon: "link" },
  reset:        { bg: "var(--indaco-warning-light)", fg: "var(--indaco-warning)", icon: "refresh" },
  new_entity:   { bg: "var(--indaco-new-entity-light)", fg: "var(--indaco-new-entity-dark)", icon: "person_add" },
};

/* ── Component ── */

export function EntityRow({
  entityType, match, verdict, correctedTo, isDerived, isParent, isReset, readOnly,
  onConfirm, onOpenSearch, onUndo, onPickAlt, extractedHint,
  pendingEntity, onConfirmWithFeedback, onCancelPending,
}: EntityRowProps) {
  const best = match.best;
  const score = best ? Math.round((best.score ?? 0) * 100) : 0;
  const isNew = best?.entity?.pk === "NEW";

  // Derive visual state
  let state: RowState;
  if (isReset) state = "reset";
  else if (isDerived) state = verdict === "corrected" ? "corrected" : "derived";
  else if (verdict === "corrected" || correctedTo) state = "corrected";
  else if (verdict === "ok") state = "confirmed";
  else if (!best || !best.entity) state = "missing";
  else if (isNew) state = "new_entity";
  else if (match.needs_review || score < 60) state = "needs_review";
  else if (score < 85) state = "needs_review";
  else state = "ok";

  const accent = ACCENT[state];
  const dot = STATUS_DOT[state];

  const displayLabel = correctedTo?.label ?? best?.display_label ?? entityFallbackLabel(best?.entity) ?? null;
  const displayPk = correctedTo?.pk ?? best?.entity?.pk ?? null;
  const displayFields = correctedTo?.fields ?? best?.entity?.fields;
  const sub = isReset ? null : subtitle(entityType.key, displayFields);

  // Filter alternatives: different PK + deduplicate by label
  const bestPk = best?.entity?.pk ?? "";
  const bestLabelLc = (best?.display_label ?? "").trim().toLowerCase();
  const alts = React.useMemo(() => {
    const seen = new Set<string>();
    if (bestLabelLc) seen.add(bestLabelLc);
    return match.alternatives.filter((a) => {
      if (a.entity.pk === bestPk) return false;
      const lbl = (a.display_label || a.entity.pk).trim().toLowerCase();
      if (seen.has(lbl)) return false;
      seen.add(lbl);
      return true;
    }).slice(0, 3);
  }, [match.alternatives, bestPk, bestLabelLc]);

  // Expandable detail panel (edificio + impianto only)
  const [detailOpen, setDetailOpen] = useState(false);
  const canShowDetails = (entityType.key === "edificio" || entityType.key === "impianto") && !!displayLabel && !isReset;
  const detailData = React.useMemo(
    () => canShowDetails ? getDetailFields(displayFields, entityType.display_fields ?? []) : { primary: [], extra: [] },
    [displayFields, entityType.display_fields, canShowDetails],
  );
  const totalDetailFields = detailData.primary.length + detailData.extra.length;

  // Inline feedback state
  const [feedbackCats, setFeedbackCats] = useState<string[]>(["match_errato"]);
  const [feedbackNote, setFeedbackNote] = useState("");
  const feedbackValid = feedbackCats.length > 0 && feedbackNote.trim().length >= 3;

  React.useEffect(() => {
    if (pendingEntity) {
      setFeedbackCats(best?.entity ? ["match_errato"] : ["match_mancante"]);
      setFeedbackNote("");
    }
  }, [pendingEntity, best?.entity]);

  return (
    <div className="card" style={{
      position: "relative", background: accent.bg, borderLeft: `3px solid ${accent.bar}`,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        {/* Icon + status dot */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: isNew ? "var(--indaco-new-entity-light)" : isDerived ? "var(--indaco-gray-100)" : "var(--indaco-brand-primary-light)",
            color: isNew ? "var(--indaco-new-entity)" : isDerived ? "var(--indaco-gray-600)" : "var(--indaco-brand-primary)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{entityType.icon}</span>
          </div>
          <span style={{
            position: "absolute", bottom: -4, right: -4,
            width: 16, height: 16, borderRadius: 999,
            background: dot.bg, color: dot.fg,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 10 }}>{dot.icon}</span>
          </span>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--indaco-gray-600)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {entityType.label}
            </span>
            {isDerived && state === "derived" && (
              <span style={{ fontSize: 10, color: "var(--indaco-gray-500)", display: "inline-flex", alignItems: "center", gap: 3 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 11 }}>link</span> derivato da edificio
              </span>
            )}
            {state === "corrected" && (
              <span style={{ fontSize: 10, color: "var(--indaco-brand-primary)", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 11 }}>edit</span> modificato
              </span>
            )}
            {state === "new_entity" && (
              <span className="badge" style={{
                background: "var(--indaco-new-entity)", color: "var(--indaco-white)",
                display: "inline-flex", alignItems: "center", gap: 3,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 11 }}>person_add</span> non in anagrafica
              </span>
            )}
          </div>

          {displayLabel ? (
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--indaco-gray-900)", marginTop: 3, lineHeight: 1.3 }}>
              {displayLabel}
            </div>
          ) : isReset ? (
            <div style={{ fontSize: 13, color: "var(--indaco-warning)", marginTop: 3, display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 500 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>refresh</span> Resettato — seleziona quello corretto
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "var(--indaco-gray-600)", fontStyle: "italic", marginTop: 3 }}>
              Nessun match trovato
              {extractedHint && <> — dall'email: <span style={{ fontWeight: 500, fontStyle: "normal" }}>"{extractedHint}"</span></>}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 5, flexWrap: "wrap" }}>
            {sub && <span style={{ fontSize: 12, color: "var(--indaco-gray-700)" }}>{sub}</span>}
            {displayPk && !isNew && <code style={{ fontSize: 10, color: "var(--indaco-gray-500)" }}>{stripPkPrefix(displayPk)}</code>}
            {entityType.key === "edificio" && codiceClienteChip(displayFields) && (
              <span className="badge badge-info" style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 11 }}>badge</span>
                {codiceClienteChip(displayFields)}
              </span>
            )}
            {best && !correctedTo && !isDerived && state !== "confirmed" && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <ConfidenceBar score={score} />
                <span style={{ fontSize: 10, color: "var(--indaco-gray-600)" }}>{methodLabel(best.method)}</span>
              </span>
            )}
            {canShowDetails && totalDetailFields > 0 && (
              <button className="btn-link" onClick={() => setDetailOpen(!detailOpen)} style={{ fontSize: 10, color: "var(--indaco-gray-500)" }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{detailOpen ? "expand_less" : "info"}</span>
                {detailOpen ? "Nascondi" : "Dettagli"}
              </button>
            )}
          </div>
        </div>

        {/* Actions — hidden when feedback form is open (pendingEntity) */}
        {!readOnly && !pendingEntity && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
            {isReset ? (
              <button className="btn btn-warning btn-sm" onClick={onOpenSearch}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>search</span>
                Seleziona
              </button>
            ) : isDerived && state === "derived" ? (
              <span style={{ fontSize: 11, color: "var(--indaco-gray-500)", padding: "0 6px" }}>auto</span>
            ) : state === "corrected" ? (<>
              <button className="btn btn-outline btn-sm" onClick={onOpenSearch}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>swap_horiz</span>
                Cambia
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onUndo}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>undo</span>
                Annulla
              </button>
            </>) : state === "confirmed" ? (<>
              <button className="btn btn-outline btn-sm" onClick={onOpenSearch}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>swap_horiz</span>
                Cambia
              </button>
              {!entityType.parent_entity && (
                <button className="btn btn-ghost btn-sm" onClick={onUndo}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>undo</span>
                  Annulla
                </button>
              )}
            </>) : isParent ? (
              state === "missing" ? (
                <button className="btn btn-outline btn-sm" onClick={onOpenSearch}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>search</span>
                  Cerca
                </button>
              ) : (
                <button className="btn btn-outline btn-sm" onClick={onOpenSearch}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>swap_horiz</span>
                  Cambia
                </button>
              )
            ) : state === "ok" ? (<>
              <button className="btn btn-outline btn-sm" onClick={onOpenSearch}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>swap_horiz</span>
                Cambia
              </button>
              <button className="btn btn-success btn-sm" onClick={onConfirm}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>check</span>
                Conferma
              </button>
            </>) : state === "needs_review" || state === "new_entity" ? (<>
              <button className="btn btn-outline btn-sm" onClick={onOpenSearch}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>swap_horiz</span>
                Cambia
              </button>
              <button className="btn btn-warning btn-sm" onClick={onConfirm}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>check</span>
                Conferma
              </button>
            </>) : state === "missing" ? (
              <button className="btn btn-primary btn-sm" onClick={onOpenSearch}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>search</span>
                Cerca
              </button>
            ) : null}
          </div>
        )}
      </div>

      {/* Expandable detail panel */}
      {detailOpen && canShowDetails && (
        <DetailPanel primary={detailData.primary} extra={detailData.extra} />
      )}

      {/* Alternatives — quick pick pills */}
      {alts.length > 0 && !readOnly && state !== "confirmed" && state !== "corrected" && !isDerived && !pendingEntity && (
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--indaco-gray-300)",
          display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--indaco-gray-500)" }}>
            Alternative
          </span>
          {alts.map((alt, i) => {
            const altCc = entityType.key === "edificio" ? codiceClienteChip(alt.entity.fields) : null;
            return (
              <button key={i} className="btn btn-outline btn-sm" onClick={() => onPickAlt(alt)}
                style={{ borderRadius: 999, padding: "2px 10px 2px 8px" }}
              >
                {alt.display_label || stripPkPrefix(alt.entity.pk)}
                {altCc && <span style={{ fontSize: 10, fontWeight: 600, color: "var(--indaco-brand-primary)" }}>{altCc}</span>}
                <code style={{ fontSize: 10, color: "var(--indaco-gray-500)" }}>{Math.round(alt.score * 100)}</code>
              </button>
            );
          })}
        </div>
      )}

      {/* Inline feedback form — visible after picking from search */}
      {pendingEntity && onConfirmWithFeedback && onCancelPending && (
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--indaco-gray-200)",
          background: "var(--indaco-brand-primary-light)", borderRadius: "var(--border-radius-md)", padding: "var(--space-2)", marginLeft: -2, marginRight: -2,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "var(--indaco-brand-primary)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>arrow_forward</span>
            {pendingEntity.label || stripPkPrefix(pendingEntity.pk)}
            <code style={{ fontSize: 10, marginLeft: 6, color: "var(--indaco-gray-500)" }}>{stripPkPrefix(pendingEntity.pk)}</code>
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
            {ENTITY_FEEDBACK_CATEGORIES.map((cat) => (
              <label key={cat.value} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "2px 8px", borderRadius: 4, fontSize: 11, cursor: "pointer",
                background: feedbackCats.includes(cat.value) ? "rgba(var(--indaco-brand-primary-rgb), 0.15)" : "rgba(255,255,255,0.6)",
              }}>
                <input type="checkbox" checked={feedbackCats.includes(cat.value)}
                  onChange={() => setFeedbackCats((prev) => prev.includes(cat.value) ? prev.filter((c) => c !== cat.value) : [...prev, cat.value])}
                  style={{ margin: 0, width: 12, height: 12 }} />
                {cat.label}
              </label>
            ))}
          </div>
          <textarea className="form-textarea" placeholder="Perché hai cambiato... (min 3 car.)" rows={2}
            value={feedbackNote} onChange={(e) => setFeedbackNote(e.target.value)}
            style={{ fontSize: 12 }} />
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button className="btn btn-primary btn-sm" disabled={!feedbackValid}
              onClick={() => onConfirmWithFeedback(pendingEntity, feedbackCats, feedbackNote)}>
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>check</span>
              Conferma
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onCancelPending}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ── */

function DetailPanel({ primary, extra }: { primary: [string, string][]; extra: [string, string][] }) {
  const [showExtra, setShowExtra] = useState(false);
  return (
    <div style={{
      marginTop: 8, padding: "8px 10px", background: "var(--indaco-gray-50)",
      borderRadius: 6, border: "1px solid var(--indaco-gray-200)",
    }}>
      {primary.length > 0 && (
        <div style={{
          display: "grid", gridTemplateColumns: "auto 1fr",
          gap: "3px 12px", fontSize: 11, lineHeight: 1.5,
        }}>
          {primary.map(([k, v]) => (
            <React.Fragment key={k}>
              <span style={{ color: "var(--indaco-gray-600)", fontWeight: 500, whiteSpace: "nowrap" }}>{fieldLabel(k)}</span>
              <span style={{ color: "var(--indaco-gray-900)", wordBreak: "break-word" }}>{v}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      {extra.length > 0 && (
        <>
          {showExtra && (
            <div style={{
              display: "grid", gridTemplateColumns: "auto 1fr",
              gap: "3px 12px", fontSize: 11, lineHeight: 1.5,
              marginTop: primary.length > 0 ? 6 : 0,
              paddingTop: primary.length > 0 ? 6 : 0,
              borderTop: primary.length > 0 ? "1px dashed var(--indaco-gray-300)" : "none",
            }}>
              {extra.map(([k, v]) => (
                <React.Fragment key={k}>
                  <span style={{ color: "var(--indaco-gray-600)", fontWeight: 500, whiteSpace: "nowrap" }}>{fieldLabel(k)}</span>
                  <span style={{ color: "var(--indaco-gray-900)", wordBreak: "break-word" }}>{v}</span>
                </React.Fragment>
              ))}
            </div>
          )}
          <button onClick={() => setShowExtra(!showExtra)} style={{
            border: 0, background: "transparent", cursor: "pointer", padding: "4px 0 0",
            display: "inline-flex", alignItems: "center", gap: 3,
            fontSize: 10, color: "var(--indaco-gray-500)", fontFamily: "var(--font-family)",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{showExtra ? "expand_less" : "expand_more"}</span>
            {showExtra ? "Meno campi" : `+${extra.length} altri campi`}
          </button>
        </>
      )}
    </div>
  );
}

function ConfidenceBar({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(100, score));
  const color = pct >= 85 ? "var(--indaco-success)" : pct >= 60 ? "var(--indaco-warning)" : "var(--indaco-danger)";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 40, height: 3, borderRadius: 2, background: "var(--indaco-gray-200)", overflow: "hidden" }}>
        <span style={{ width: `${pct}%`, height: "100%", background: color, display: "block", transition: "width 300ms" }} />
      </span>
      <code style={{ fontSize: 10, fontWeight: 600, color }}>{pct}</code>
    </span>
  );
}
